import { Price } from '@/types';
import { startOfDay, toDate } from '@/utils/datetime';

/**
 * Client-side mirror of the backend price validity filter
 * (legacy-api ServiceExtensions.LoadPricesAsync, ServiceExtensions.cs:249-282).
 *
 * A recurring series is booked against ONE service fetch, so the category list the
 * UI holds is a union over the whole series. This predicate decides, per
 * (occurrence, price), whether the backend would consider the price valid on that
 * occurrence's own date/time/weekday — entirely `now`-independent.
 */

/** `To = '9999-12-31T00:00:00'` is the backend's open-ended sentinel (DateTime.MaxValue). */
const OPEN_ENDED_YEAR = 9999;

/** 'HH:mm:ss' | 'HH:mm' → seconds of day. Returns null when unparsable/absent. */
function secondsOfDay(value?: string | null): number | null {
    if (!value) return null;
    const parts = value.split(':');
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    const seconds = parts.length > 2 ? Number(parts[2]) : 0;
    if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) return null;
    return hours * 3600 + minutes * 60 + seconds;
}

/** Backend: `.Date` truncation on BOTH sides, BOTH ends inclusive. */
function isDateInRange(occurrence: Date, price: Price): boolean {
    const day = startOfDay(occurrence);

    if (price.From) {
        const from = toDate(price.From);
        if (!Number.isNaN(from.getTime()) && day < startOfDay(from)) return false;
    }

    if (!price.To) return true;
    const to = toDate(price.To);
    if (Number.isNaN(to.getTime()) || to.getFullYear() >= OPEN_ENDED_YEAR) return true;
    return day <= startOfDay(to);
}

/**
 * Backend compares `(int)priceDate.DayOfWeek` (Sunday = 0) against
 * `DayOfWeeks.Select(vd => vd.DayOfWeek.ToDotNetDayOfWeek())`.
 * A price with no weekday rows is backfilled to all seven days server-side
 * (ServicePriceExtensions.LoadValidDaysAsync), so "no rows" means EVERY day, never none.
 */
function isWeekdayMatch(occurrence: Date, price: Price): boolean {
    const days = price.DaysOfWeek ?? [];
    if (days.length === 0) return true;

    const dotNetDay = occurrence.getDay(); // 0 = Sunday
    return days.some((day) =>
        typeof day.DotNetDayOfWeekId === 'number'
            ? day.DotNetDayOfWeekId === dotNetDay
            : day.DayOfWeekId === (dotNetDay === 0 ? 7 : dotNetDay) // 1 = Monday .. 7 = Sunday
    );
}

/** Backend: FromTime inclusive, ToTime EXCLUSIVE, with an overnight-wrap branch. */
function isTimeOfDayMatch(occurrence: Date, price: Price): boolean {
    const from = secondsOfDay(price.FromTime);
    const to = secondsOfDay(price.ToTime);
    if (from === null || to === null) return true;

    // The client used to send `PriceTime` as HH:mm, so seconds are dropped here too.
    const at = occurrence.getHours() * 3600 + occurrence.getMinutes() * 60;

    return to < from
        ? at < to || at >= from // overnight, e.g. 22:00 → 02:00
        : at >= from && at < to;
}

export function isPriceValidForOccurrence(price: Price, occurrenceStart: Date | string): boolean {
    const occurrence = toDate(occurrenceStart);
    if (Number.isNaN(occurrence.getTime())) return true; // fail open — never hide a category on bad input

    return (
        isDateInRange(occurrence, price) &&
        isWeekdayMatch(occurrence, price) &&
        isTimeOfDayMatch(occurrence, price)
    );
}

export function getValidPriceIds(prices: Price[], occurrenceStart: Date | string): Set<number> {
    return new Set(
        (prices || []).filter((p) => isPriceValidForOccurrence(p, occurrenceStart)).map((p) => p.Id)
    );
}
